import React, { useEffect, useState } from 'react';
import moment from 'moment';
import axios from 'axios';
import Modal from 'react-modal';
import Select from 'react-select';
import { Checkbox } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Tooltip from '../../common/Tooltip';
import LoadingSpinner from '../../common/LoadingSpinner';
import { getCSRFToken, ROOT_URL_V2 } from '../../../config';
import { ErrorMessage } from '../../common';
import { camelToSnake, getErrorMessages } from '../../../utils';
import { TPageNotification, TSeries } from '../../../types';

type TFields = {
    clientPayPerHour: string;
    freelancerPayPerHour: string;
    freelancers: number[];
    fromDate: string;
    jobsOnly: boolean;
}

type TModalConfirmEditRatesProps = {
    isOpen: boolean;
    isLoading: boolean;
    handleClose: () => void;
    handleConfirm: () => void,
    fields: TFields,
    series: TSeries;
    freelancerOptions: {
        label: string;
        value: number;
    }[];
}

export function ModalConfirmEditRates({
    isOpen,
    isLoading,
    handleClose,
    handleConfirm,
    fields,
    series,
    freelancerOptions,
}: TModalConfirmEditRatesProps) {

    const freelancerNames = freelancerOptions.filter(
        freelancer => fields.freelancers.includes(freelancer.value)
    ).map(
        freelancer => freelancer.label
    ).join(', ');

    return (
        <Modal
            className="confirm-edit-rates-modal"
            isOpen={isOpen}
            onRequestClose={handleClose}
            contentLabel="Adjust rates"
            ariaHideApp={false}
        >
            <p className="modal-title">Adjust rates</p>
            <div className="confirm-edit-rates-body">
                <p>Are you sure you want to adjust rates on the ongoing job <b>{series.id}</b>?</p>
                <p>From date: <b>{moment(fields.fromDate).format('DD/MM/YYYY')}</b></p>
                <p>Pay Rate: <b>{fields.freelancerPayPerHour ? `£${fields.freelancerPayPerHour}/h` : 'unchanged'}</b></p>
                <p>Charge Rate: <b>{fields.clientPayPerHour ? `£${fields.clientPayPerHour}/h` : 'unchanged'}</b></p>
                {fields.jobsOnly
                    ? <p><b>Job requirement only</b></p>
                    : <p>Job requirement and bookings: <b>{freelancerNames}</b></p>
                }
            </div>

            <button
                className="continue btn--round-big"
                onClick={handleConfirm}
                type="button"
                disabled={isLoading}
            >
                {isLoading ? (
                    <LoadingSpinner
                        className="edit-series-spinner"
                        backgroundColor="white"
                    />
                ) : (
                    'CONFIRM'
                )}
            </button>
            {!isLoading && (
                <button className="back" onClick={handleClose} type="button">
                    CANCEL
                </button>
            )}
        </Modal>
    );
}

type TModalEditRatesProps = {
    notification: TPageNotification;
    reloadData: () => void;
    handleClose: (modalName: string) => void;
    isOpen: boolean;
    series: TSeries;
}

export function ModalEditRates({
    notification,
    reloadData,
    handleClose,
    isOpen,
    series,
}: TModalEditRatesProps) {

    const [fields, setFields] = useState<TFields>({
        clientPayPerHour: '',
        freelancerPayPerHour: '',
        freelancers: null,
        fromDate: null,
        jobsOnly: false,
    });
    const [isValid, setIsValid] = useState(false);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalConfirmEditRatesOpen, setIsModalConfirmEditRatesOpen] = useState(false);

    const currentDate = new Date;
    const firstJobDate = new Date(series.first_job_start);
    const [selectedDate, setSelectedDate] = useState(currentDate >= firstJobDate ? currentDate : firstJobDate);
    const [selectedFreelancers, setSelectedFreelancers] = useState([]);
    const [freelancers, setFreelancers] = useState([]);

    useEffect(() => {

        async function getOptions() {
            if (isOpen) {
                try {
                    const response = await axios({
                        method: 'GET',
                        url: `${ROOT_URL_V2}series/${series.id}/freelancers/`,
                        headers: {
                            'X-CSRFToken': getCSRFToken(),
                        },
                    });
                    setFreelancers(response.data.content);
                } catch (err) {
                    setError(getErrorMessages(err));
                }
            }
        } getOptions();
    }, [isOpen, series.id]);

    const freelancerOptions = freelancers.map(freelancer => ({
        label: freelancer.full_name,
        value: freelancer.id
    }));

    const handleOpenModalConfirmEditRates = (): void => {
        setIsModalConfirmEditRatesOpen(true);
    };

    const handleCloseModalConfirmEditRates = (): void => {
        setIsModalConfirmEditRatesOpen(false);
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        setFields(prevFields => ({
            ...prevFields,
            [fieldName]: value,
        }));
    };

    const handleBlur = (fieldName: 'freelancerPayPerHour' | 'clientPayPerHour') => {
        setFields(prevFields => ({
            ...prevFields,
            [fieldName]: Number(prevFields[fieldName]).toFixed(2),
        }));
    };

    const fieldsAreValid = (): void => {
        if (
            !(fields.clientPayPerHour || fields.freelancerPayPerHour) ||
            !fields.fromDate ||
            fields.fromDate === 'Invalid date' ||
            (!fields.jobsOnly && !fields.freelancers.length)
        ) {
            setIsValid(false);
        } else {
            setIsValid(true);
        }
    };

    useEffect(() => {
        const date = moment(selectedDate).format('YYYY-MM-DD');
        handleFieldChange('fromDate', date);
    }, [selectedDate]);

    useEffect(() => {
        const currentFreelancers = selectedFreelancers.map(freelancer => freelancer.value);
        handleFieldChange('freelancers', currentFreelancers);
    }, [selectedFreelancers]);

    useEffect(() => {
        fieldsAreValid();
    }, [fields]);

    const cleanPayloadData = (obj: TFields) => {
        const cleanedFields = { ...obj };

        if (cleanedFields.jobsOnly) {
            delete cleanedFields.freelancers;
        }
        if (!cleanedFields.freelancerPayPerHour) {
            delete cleanedFields.freelancerPayPerHour;
        }
        if (!cleanedFields.clientPayPerHour) {
            delete cleanedFields.clientPayPerHour;
        }

        return cleanedFields;
    };

    const handleContinue = (): void => {
        setError(null);
        handleOpenModalConfirmEditRates();
    };

    const handleConfirm = (): void => {
        if (isValid) {
            setIsLoading(true);

            const payloadData = cleanPayloadData(fields);

            axios({
                method: 'patch',
                url: `${ROOT_URL_V2}series/${series.id}/update_series/`,
                data: camelToSnake(payloadData),
                headers: {
                    'X-CSRFToken': getCSRFToken(),
                },
            }).then(() => {
                setIsLoading(false);
                handleCloseModalConfirmEditRates();
                handleClose('EditRates');

                notification.success(
                    'Success',
                    `${series.series_reference_number} successfully edited`
                );

                reloadData();
            }).catch(err => {
                setIsLoading(false);
                setError(getErrorMessages(err));
                handleCloseModalConfirmEditRates();
            });
        }
    };

    const handleDayChange = (date: Date) => {
        setSelectedDate(date);
    };

    const handleCheckboxChange = () => {
        handleFieldChange('jobsOnly', !fields.jobsOnly);
        if (selectedFreelancers.length) {
            setSelectedFreelancers([]);
        }
    };

    const handleSelect = (selectedOption: any[]) => {
        setSelectedFreelancers(selectedOption);
    };

    return (
        <Modal
            className="edit-rates-modal"
            isOpen={isOpen}
            onRequestClose={() => handleClose('EditRates')}
            contentLabel="Adjust rates"
            ariaHideApp={false}
        >
            <p className="modal-title">Adjust rates</p>
            <table className="edit-rates-body">
                <tbody>
                    <tr>
                        <td className="edit-rates-label">From date</td>
                        <td>
                            <DatePicker
                                className="edit-rates-date-field"
                                selected={selectedDate}
                                onChange={date => handleDayChange(date)}
                                showMonthDropdown={true}
                                showYearDropdown={true}
                                dropdownMode="select"
                                dateFormat="dd/MM/yyyy"
                                minDate={new Date(series.first_job_start)}
                                maxDate={new Date(series.last_job_start)}
                                calendarStartDay={1}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="edit-rates-label">
                            Job requirement only
                            <Tooltip tooltip='Check if you do not want to update workers already assigned to bookings'>
                                <i className="icon icon-infomation-circle margin-5-px" />
                            </Tooltip>
                        </td>
                        <td>
                            <Checkbox
                                className="edit-rates-checkbox checkbox-purple"
                                label="Job requirement only"
                                // @ts-ignore
                                value={fields.jobsOnly}
                                onChange={handleCheckboxChange}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="edit-rates-label">Select bookings</td>
                        <td>
                            <Select
                                name="select-freelancer"
                                value={selectedFreelancers}
                                onChange={handleSelect}
                                options={freelancerOptions}
                                multi={true}
                                disabled={fields.jobsOnly}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="edit-rates-label">Workers rate</td>
                        <td className="edit-series-rates-field">
                            <input
                                type="number"
                                step="0.25"
                                id="freelancer_pay_per_hour"
                                value={fields.freelancerPayPerHour}
                                placeholder={`Current: ${series.freelancer_pay_per_hour.toFixed(2)}`}
                                onChange={event => {
                                    handleFieldChange('freelancerPayPerHour', event.target.value);
                                }}
                                onBlur={() => handleBlur('freelancerPayPerHour')}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="edit-rates-label">Client rate</td>
                        <td className="edit-series-rates-field">
                            <input
                                type="number"
                                step="0.25"
                                id="client_pay_per_hour"
                                value={fields.clientPayPerHour}
                                placeholder={`Current: ${series.client_pay_per_hour.toFixed(2)}`}
                                onChange={event => {
                                    handleFieldChange('clientPayPerHour', event.target.value);
                                }}
                                onBlur={() => handleBlur('clientPayPerHour')}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>

            <button
                className="continue btn--round-big"
                onClick={handleContinue}
                type="button"
                disabled={!isValid}
            >
                CONTINUE
            </button>
            <button className="back" onClick={() => handleClose('EditRates')} type="button">
                CANCEL
            </button>

            {!!error && <ErrorMessage message={error} />}

            {isModalConfirmEditRatesOpen &&
                <ModalConfirmEditRates
                    isOpen={isModalConfirmEditRatesOpen}
                    isLoading={isLoading}
                    handleClose={handleCloseModalConfirmEditRates}
                    handleConfirm={handleConfirm}
                    fields={fields}
                    series={series}
                    freelancerOptions={freelancerOptions}
                />
            }
        </Modal>
    );
}
